function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/*
 * Audiences > Suppression (redesign 2026-10-09, PlunkDocs mockups/audiences-redesign-2026-10-09.html board "6 · Suppression").
 * CampaignService:
 *   GET    /v1/campaign/email/suppression?reason&q&$top&$skip > {items:[{id,email,reason,rail,source,campaignId,
 *          personId,expiresDate,note,createdBy,createdDate}], totalCount, reasonCounts:{reason:n}}
 *   GET    suppression/stats > {total, byReason{}, expiredSoft, ...}                    (soft-fails)
 *   POST   suppression {emails[], reason, note}
 *   POST   suppression/purge-expired > {removed}                                        (Administrator)
 *   DELETE suppression/{id}         Administrator; 409 permanent for complaint / erased
 *   POST   suppression/import       multipart CSV (reason, note, preview=true > preview report)
 *   GET    suppression/export?reason
 *
 * The list endpoint filters on one reason. A pill that covers several reasons fetches each one
 * (first skip+PAGE rows), merges newest first and slices the page, so paging stays exact.
 */
import React from "react";
import { withRouter } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { Empty, Pending } from "../../components/analytics-kit";
import Error from "../../components/error";
import { num } from "../sales/territory-common";
import AudiencesShell, { SUBTITLES } from "./shell";
import { getSuppression, addSuppression, removeSuppression, importSuppression, exportSuppression, getSuppressionStats, purgeExpiredSuppression, isAdministrator, isSoft, toast } from "./api";
import { Modal, ErrorLine, InlineConfirm, Banner, fmtDate, RAIL_LABELS } from "./ui";
const PAGE = 50;
const NO_ALLOW_AGAIN = ["complaint", "erased", "unsubscribe"];
const ADDABLE = ["manual", "unsubscribe", "hard_bounce", "invalid_syntax", "role_address", "disposable", "global_block"];
const BOUNCES = ["hard_bounce", "soft_bounce_x3", "provider_cleaned"];

/** Filter pills: key, label, reasons ([] = all). */
const FILTERS = [["all", "All", []], ["stop", "Asked to stop", ["unsubscribe"]], ["gone", "Doesn't exist", ["hard_bounce", "invalid_syntax", "provider_cleaned"]], ["spam", "Marked as spam", ["complaint"]], ["retry", "Will try again", ["soft_bounce_x3"]], ["you", "Added by you", ["manual"]]];

/** Plain labels for the reason picker in the add and upload dialogs. */
const ADD_LABELS = {
  manual: "Don't email (my choice)",
  unsubscribe: "Asked to stop",
  hard_bounce: "Address doesn't exist",
  invalid_syntax: "Not a valid address",
  role_address: "Shared inbox (like info@)",
  disposable: "Throwaway address",
  global_block: "Never email anyone at this address"
};

/** Turn provider bounce text (SMTP codes and phrases) into a plain reason. */
function bounceKind(text, soft) {
  const t = String(text || "");
  if (/\b[45]\.2\.2\b|mailbox (is )?full|over ?quota|quota exceeded|insufficient storage/i.test(t)) return "Mailbox full";
  if (/spam|blacklist|block ?list|blocked|reputation|\b5\.7\.1\b|policy/i.test(t)) return "Blocked as spam";
  if (/\b5\.1\.[0-9]\b|user unknown|unknown user|no such (user|mailbox)|does not exist|doesn't exist|invalid (recipient|address|mailbox)|not found|recipient rejected|address rejected/i.test(t)) return "Address doesn't exist";
  if (/refused|rejected|denied|\b[45]\.[0-9]\.[0-9]\b|timed? ?out|deferred|unavailable/i.test(t)) return "Server refused";
  return soft ? "Server refused" : "Address doesn't exist";
}
function whyText(r) {
  switch (r.reason) {
    case "unsubscribe":
      return "Asked to stop";
    case "complaint":
      return "Marked as spam";
    case "manual":
      return "Added by you";
    case "erased":
      return "Erased on request";
    case "invalid_syntax":
      return "Not a valid address";
    case "role_address":
      return "Shared inbox";
    case "disposable":
      return "Throwaway address";
    case "global_block":
      return "Blocked for everyone";
    case "hard_bounce":
    case "provider_cleaned":
      return bounceKind(r.note, false);
    case "soft_bounce_x3":
      return bounceKind(r.note, true);
    default:
      {
        const t = String(r.reason || "").replace(/_/g, " ");
        return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Unknown";
      }
  }
}
const whyTone = reason => reason === "complaint" || reason === "erased" ? "bad" : reason === "soft_bounce_x3" ? "warn" : BOUNCES.includes(reason) || reason === "invalid_syntax" ? "bad" : "";

/** {text, details}: a plain sentence, plus raw provider text that only shows behind the details toggle. */
function howText(r) {
  const rail = r.rail && r.rail !== "plunk" ? RAIL_LABELS[r.rail] || r.rail : null;
  const src = String(r.source || "").toLowerCase();
  const note = r.note ? String(r.note) : "";
  if (BOUNCES.includes(r.reason)) {
    const what = r.campaignName ? r.campaignName : r.rail === "outreach" ? "an outreach email" : r.campaignId ? "an email campaign" : "an email";
    return {
      text: `Bounced on ${what}`,
      details: note || null
    };
  }
  if (r.reason === "complaint") return {
    text: rail && rail !== "outreach" ? `Reported as spam in ${rail}` : "Reported as spam",
    details: note && /webhook|:/.test(note) ? note : null
  };
  if (r.reason === "unsubscribe") {
    if (/one-click/i.test(note)) return {
      text: "One-click unsubscribe"
    };
    if (src === "link" || src === "unsubscribe_link") return {
      text: "Unsubscribe link"
    };
    if (rail) return {
      text: `Unsubscribed in ${rail}`
    };
    if (src === "csv") return {
      text: "From an uploaded list"
    };
    return {
      text: "Asked to stop"
    };
  }
  if (r.reason === "erased") return {
    text: "Privacy request"
  };
  if (src === "csv") return {
    text: note && note !== "csv import" ? `Uploaded list: "${note}"` : "From an uploaded list"
  };
  if (r.reason === "manual") return {
    text: note ? `"${note}"` : "Added by hand"
  };
  if (note) return {
    text: `"${note}"`
  };
  return {
    text: rail ? `From ${rail}` : "Found by Plunk's address check"
  };
}
function untilText(r) {
  let exp = r.expiresDate;
  if (!exp && r.reason === "soft_bounce_x3" && r.createdDate) {
    const d = new Date(r.createdDate);
    if (!isNaN(d)) {
      d.setDate(d.getDate() + 30);
      exp = d.toISOString();
    }
  }
  if (!exp) return "Permanent";
  const d = new Date(exp);
  if (!isNaN(d) && d <= new Date()) return "Ready to try again";
  return `Tries again ${fmtDate(exp)}`;
}
function shortDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric"
  });
}
class SuppressionComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onSearch", q => {
      this.setState({
        q
      });
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this.load(0), 350);
    });
    _defineProperty(this, "pick", filter => {
      if (filter !== this.state.filter) this.setState({
        filter
      }, () => this.load(0));
    });
    _defineProperty(this, "add", async () => {
      const emails = this.state.addText.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
      if (!emails.length) {
        this.setState({
          err: "Paste at least one address."
        });
        return;
      }
      this.setState({
        busy: true,
        err: ""
      });
      try {
        const r = await addSuppression(emails, this.state.addReason, this.state.addNote || undefined);
        toast(`${num(r.added)} added · ${num(r.alreadyPresent)} were already here.`);
        this.setState({
          dialog: null,
          addText: "",
          addNote: ""
        });
        this.load(0);
        this.loadStats();
      } catch (e) {
        const items = e.data && e.data.items;
        this.setState({
          err: (e.message || "Couldn't add them") + (items && items.length ? `: ${items.join(", ")}` : "")
        });
      } finally {
        if (this._alive) this.setState({
          busy: false
        });
      }
    });
    _defineProperty(this, "importFile", async preview => {
      if (!this.state.file) {
        this.setState({
          err: "Choose a CSV file."
        });
        return;
      }
      this.setState({
        busy: true,
        err: ""
      });
      try {
        const r = await importSuppression(this.state.file, this.state.addReason, this.state.addNote || undefined, preview);
        if (!this._alive) return;
        if (preview) {
          // A build without preview support imports straight away: treat a result carrying `added` as committed.
          if (r && r.added != null && r.parsed != null && r.preview !== true) {
            this.setState({
              result: r
            });
            this.load(0);
            this.loadStats();
          } else this.setState({
            preview: r || {}
          });
        } else {
          this.setState({
            result: r,
            preview: null
          });
          this.load(0);
          this.loadStats();
        }
      } catch (e) {
        if (preview && isSoft(e)) {
          this.setState({
            err: "Checking first isn't available here yet. Upload adds the addresses directly."
          });
        } else this.setState({
          err: e.message || "Upload failed"
        });
      } finally {
        if (this._alive) this.setState({
          busy: false
        });
      }
    });
    _defineProperty(this, "purgeExpired", async () => {
      this.setState({
        purging: true
      });
      try {
        const r = await purgeExpiredSuppression();
        const msg = `${num(r && r.removed)} expired bounces cleared. Those addresses can get bulk email again.`;
        toast(msg);
        this.setState({
          confirmPurge: false,
          purging: false,
          note: msg
        });
        this.load(0);
        this.loadStats();
      } catch (e) {
        this.setState({
          confirmPurge: false,
          purging: false
        });
        toast(isSoft(e) ? "Clearing expired bounces isn't available here yet." : e.status === 403 || e.status === 401 ? "Only an Administrator can do this." : e.message || "Couldn't clear them");
      }
    });
    _defineProperty(this, "remove", async row => {
      this.setState({
        busy: true
      });
      try {
        await removeSuppression(String(row.id));
        toast(`${row.email} can get bulk email again.`);
        this.setState({
          confirm: null
        });
        this.load(this.state.skip);
        this.loadStats();
      } catch (e) {
        toast(e.status === 409 ? "This address can't be allowed again." : e.status === 403 || e.status === 401 ? "Only an Administrator can allow an address again." : e.message || "Couldn't allow it again");
      } finally {
        if (this._alive) this.setState({
          busy: false
        });
      }
    });
    _defineProperty(this, "export", async () => {
      const reasons = this.reasons();
      try {
        await exportSuppression(reasons.length === 1 ? reasons[0] : undefined);
      } catch (e) {
        toast(e.message || "Export failed");
      }
    });
    this.state = {
      data: null,
      error: false,
      filter: "all",
      q: "",
      skip: 0,
      dialog: null,
      busy: false,
      err: "",
      addText: "",
      addReason: "manual",
      addNote: "",
      file: null,
      preview: null,
      result: null,
      stats: undefined,
      confirmPurge: false,
      purging: false,
      note: "",
      confirm: null,
      open: {}
    };
    this._alive = true;
    this._debounce = null;
    this._seq = 0;
  }
  componentDidMount() {
    this.load();
    this.loadStats();
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._debounce);
  }
  reasons() {
    return (FILTERS.find(f => f[0] === this.state.filter) || FILTERS[0])[2];
  }
  async load(skip = 0) {
    const seq = ++this._seq;
    this.setState({
      data: null,
      skip,
      confirm: null
    });
    const reasons = this.reasons();
    const q = this.state.q || undefined;
    try {
      let r;
      if (reasons.length <= 1) {
        r = await getSuppression(reasons[0] || undefined, q, PAGE, skip);
      } else {
        const parts = await Promise.all(reasons.map(x => getSuppression(x, q, skip + PAGE, 0).catch(e => {
          if (e && e.status === 400) return null;
          throw e;
        })));
        const ok = parts.filter(Boolean);
        const merged = ok.flatMap(p => p.items || []).sort((a, b) => String(b.createdDate || "").localeCompare(String(a.createdDate || "")));
        r = {
          items: merged.slice(skip, skip + PAGE),
          totalCount: ok.reduce((a, p) => a + (Number(p.totalCount) || 0), 0),
          reasonCounts: ok[0] && ok[0].reasonCounts || {}
        };
      }
      if (!this._alive || seq !== this._seq) return;
      this.setState({
        data: r || {
          items: [],
          totalCount: 0,
          reasonCounts: {}
        }
      });
    } catch (e) {
      if (this._alive && seq === this._seq) this.setState({
        error: true
      });
    }
  }
  async loadStats() {
    try {
      const s = await getSuppressionStats();
      if (this._alive) this.setState({
        stats: s || null
      });
    } catch (e) {
      if (this._alive) this.setState({
        stats: null
      });
    }
  }
  renderDialog() {
    const {
      dialog,
      busy,
      err,
      addText,
      addReason,
      addNote,
      preview: p,
      result,
      file
    } = this.state;
    if (!dialog) return null;
    const close = () => this.setState({
      dialog: null,
      err: "",
      result: null,
      preview: null,
      file: null
    });
    const reasonPick = /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Why"), /*#__PURE__*/React.createElement("select", {
      className: "ec-input",
      value: addReason,
      onChange: e => this.setState({
        addReason: e.target.value
      })
    }, ADDABLE.map(r => /*#__PURE__*/React.createElement("option", {
      key: r,
      value: r
    }, ADD_LABELS[r] || r))));
    const notePick = /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Note (optional)"), /*#__PURE__*/React.createElement("input", {
      value: addNote,
      onChange: e => this.setState({
        addNote: e.target.value
      }),
      placeholder: "For example: asked by phone not to email"
    }));
    if (dialog === "add") {
      return /*#__PURE__*/React.createElement(Modal, {
        title: "Add addresses",
        width: 560,
        onClose: close,
        busy: busy,
        note: "These addresses never get bulk email, whatever list they are on.",
        footer: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
          onClick: close,
          disabled: busy
        }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
          themeColor: "primary",
          disabled: busy,
          onClick: this.add
        }, busy ? "Adding…" : "Add"))
      }, /*#__PURE__*/React.createElement("div", {
        className: "ec-field"
      }, /*#__PURE__*/React.createElement("label", null, "Addresses"), /*#__PURE__*/React.createElement("textarea", {
        className: "ec-input",
        rows: 6,
        autoFocus: true,
        value: addText,
        onChange: e => this.setState({
          addText: e.target.value
        }),
        placeholder: "name@example.com"
      }), /*#__PURE__*/React.createElement("div", {
        className: "ec-help"
      }, "One per line, or separated by commas.")), reasonPick, notePick, /*#__PURE__*/React.createElement(ErrorLine, null, err));
    }
    const report = (x, committed) => /*#__PURE__*/React.createElement("div", {
      className: "aud-funnel"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aud-f"
    }, /*#__PURE__*/React.createElement("span", null, "Rows read"), /*#__PURE__*/React.createElement("span", {
      className: "v"
    }, num(x.parsed != null ? x.parsed : x.rows))), /*#__PURE__*/React.createElement("div", {
      className: "aud-f"
    }, /*#__PURE__*/React.createElement("span", null, committed ? "Added" : "Will be added"), /*#__PURE__*/React.createElement("span", {
      className: "v"
    }, num(x.added != null ? x.added : x.wouldAdd))), /*#__PURE__*/React.createElement("div", {
      className: "aud-f"
    }, /*#__PURE__*/React.createElement("span", null, "Already here"), /*#__PURE__*/React.createElement("span", {
      className: "v"
    }, num(x.alreadyPresent))), /*#__PURE__*/React.createElement("div", {
      className: "aud-f"
    }, /*#__PURE__*/React.createElement("span", null, "Not valid addresses"), /*#__PURE__*/React.createElement("span", {
      className: "v"
    }, num(x.invalid))), x.invalidSamples && x.invalidSamples.length ? /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "For example: ", x.invalidSamples.slice(0, 5).join(", ")) : null, x.samples && x.samples.length ? /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Sample: ", x.samples.slice(0, 5).join(", ")) : null);
    return /*#__PURE__*/React.createElement(Modal, {
      title: "Upload a do-not-email list",
      width: 560,
      onClose: close,
      busy: busy,
      note: p && !result ? "Nothing is saved until you upload." : null,
      footer: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
        onClick: close,
        disabled: busy
      }, result ? "Done" : "Cancel"), !result && !p ? /*#__PURE__*/React.createElement(Button, {
        themeColor: "primary",
        disabled: busy || !file,
        onClick: () => this.importFile(true)
      }, busy ? "Checking…" : "Check the file") : null, !result && p ? /*#__PURE__*/React.createElement(Button, {
        themeColor: "primary",
        disabled: busy,
        onClick: () => this.importFile(false)
      }, busy ? "Uploading…" : `Upload${p.wouldAdd != null ? ` ${num(p.wouldAdd)}` : ""}`) : null)
    }, result ? report(result, true) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "CSV file"), /*#__PURE__*/React.createElement("input", {
      type: "file",
      accept: ".csv,text/csv",
      onChange: e => this.setState({
        file: e.target.files && e.target.files[0],
        preview: null,
        err: ""
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Addresses in the first column. A header row is fine. Every address gets the reason you pick here.")), reasonPick, notePick, p ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h4", {
      className: "aud-h4"
    }, "What will happen"), report(p, false)) : null, /*#__PURE__*/React.createElement(ErrorLine, null, err)));
  }
  renderRow(r, admin) {
    const {
      confirm,
      busy,
      open
    } = this.state;
    const id = String(r.id);
    const isHash = r.reason === "erased" || r.email && !r.email.includes("@") && r.email.length > 20;
    const how = howText(r);
    const canAllow = admin && !NO_ALLOW_AGAIN.includes(r.reason);
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: id
    }, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "asp-email"
    }, isHash && r.email ? `Erased (${r.email.replace(/^sha256:/i, "").slice(0, 6)}…)` : r.email || ""), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", {
      className: `asp-why ${whyTone(r.reason)}`
    }, whyText(r))), /*#__PURE__*/React.createElement("td", {
      className: "asp-how"
    }, how.text, how.details ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "asp-link",
      "aria-expanded": !!open[id],
      onClick: () => this.setState({
        open: {
          ...open,
          [id]: !open[id]
        }
      })
    }, "details") : null), /*#__PURE__*/React.createElement("td", {
      className: "asp-when"
    }, shortDate(r.createdDate)), /*#__PURE__*/React.createElement("td", {
      className: "asp-until"
    }, untilText(r)), /*#__PURE__*/React.createElement("td", {
      className: "asp-act"
    }, !canAllow ? null : confirm && String(confirm.id) === id ? /*#__PURE__*/React.createElement(InlineConfirm, {
      variant: "row",
      question: "Allow bulk email again?",
      yes: "Yes, allow",
      busy: busy,
      onYes: () => this.remove(r),
      onNo: () => this.setState({
        confirm: null
      })
    }) : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "asp-link",
      onClick: () => this.setState({
        confirm: r
      })
    }, "Allow again"))), how.details && open[id] ? /*#__PURE__*/React.createElement("tr", {
      className: "asp-details"
    }, /*#__PURE__*/React.createElement("td", {
      colSpan: 6
    }, /*#__PURE__*/React.createElement("code", null, how.details))) : null);
  }
  render() {
    const {
      data,
      error,
      filter,
      q,
      skip,
      stats,
      confirmPurge,
      purging,
      note
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    const items = data ? data.items || [] : [];
    const total = data ? Number(data.totalCount) || 0 : 0;
    const admin = isAdministrator();
    const by = stats && stats.byReason || (data && filter === "all" && !q ? data.reasonCounts : null) || {};
    const n = (...ks) => ks.reduce((a, k) => a + (Number(by[k]) || 0), 0);
    const grand = stats && stats.total != null ? Number(stats.total) : Object.keys(by).reduce((a, k) => a + (Number(by[k]) || 0), 0);
    const haveSummary = stats !== undefined || data;
    const expired = stats ? Number(stats.expiredSoft) || 0 : 0;
    const outline = {
      fillMode: "outline"
    };
    return /*#__PURE__*/React.createElement(AudiencesShell, {
      subtitle: SUBTITLES.suppression,
      cls: "asp-view",
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, _extends({}, outline, {
        onClick: this.export
      }), "Export"), /*#__PURE__*/React.createElement(Button, _extends({}, outline, {
        onClick: () => this.setState({
          dialog: "import",
          err: "",
          result: null,
          preview: null,
          file: null
        })
      }), "Upload a do-not-email list"), /*#__PURE__*/React.createElement(Button, _extends({}, outline, {
        onClick: () => this.setState({
          dialog: "add",
          err: ""
        })
      }), "Add addresses"))
    }, note ? /*#__PURE__*/React.createElement(Banner, {
      onClose: () => this.setState({
        note: ""
      })
    }, note) : null, /*#__PURE__*/React.createElement("section", {
      className: "ah-card ah-summary"
    }, !haveSummary ? /*#__PURE__*/React.createElement(Pending, {
      h: 48
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "ah-verdict"
    }, /*#__PURE__*/React.createElement("b", null, num(grand), " ", grand === 1 ? "address never gets" : "addresses never get", " bulk email"), /*#__PURE__*/React.createElement("span", null, "Plunk adds them for you when an email bounces, someone unsubscribes or marks you as spam. They still see your ads.")), /*#__PURE__*/React.createElement("span", {
      className: "ah-stat"
    }, /*#__PURE__*/React.createElement("b", null, num(n("unsubscribe"))), /*#__PURE__*/React.createElement("span", null, "asked to stop")), /*#__PURE__*/React.createElement("span", {
      className: "ah-stat"
    }, /*#__PURE__*/React.createElement("b", null, num(n("hard_bounce", "invalid_syntax", "provider_cleaned"))), /*#__PURE__*/React.createElement("span", null, "don't exist")), /*#__PURE__*/React.createElement("span", {
      className: "ah-stat"
    }, /*#__PURE__*/React.createElement("b", null, num(n("complaint"))), /*#__PURE__*/React.createElement("span", null, "marked as spam")), /*#__PURE__*/React.createElement("span", {
      className: "ah-stat"
    }, /*#__PURE__*/React.createElement("b", null, num(n("manual"))), /*#__PURE__*/React.createElement("span", null, "added by you")))), /*#__PURE__*/React.createElement("section", {
      className: "ah-card asp-table-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "asp-toolbar"
    }, /*#__PURE__*/React.createElement("div", {
      className: "asp-pills",
      role: "tablist",
      "aria-label": "Filter by reason"
    }, FILTERS.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      role: "tab",
      "aria-selected": filter === k,
      className: `asp-pill${filter === k ? " on" : ""}`,
      onClick: () => this.pick(k)
    }, l))), /*#__PURE__*/React.createElement("input", {
      type: "search",
      className: "asp-search",
      value: q,
      placeholder: "Search address",
      "aria-label": "Search address",
      onChange: e => this.onSearch(e.target.value)
    })), admin && expired > 0 ? /*#__PURE__*/React.createElement("div", {
      className: "asp-expired"
    }, confirmPurge ? /*#__PURE__*/React.createElement(InlineConfirm, {
      question: `Clear ${num(expired)} expired ${expired === 1 ? "bounce" : "bounces"}? Those addresses can get bulk email again.`,
      yes: "Yes, clear",
      busy: purging,
      onYes: this.purgeExpired,
      onNo: () => this.setState({
        confirmPurge: false
      })
    }) : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "asp-link",
      disabled: purging,
      onClick: () => this.setState({
        confirmPurge: true
      })
    }, "Clear ", num(expired), " expired ", expired === 1 ? "bounce" : "bounces")) : null, !data ? /*#__PURE__*/React.createElement(Pending, null) : items.length === 0 ? /*#__PURE__*/React.createElement(Empty, null, q || filter !== "all" ? "No addresses match. Clear the search or pick another filter." : "No addresses yet. Plunk adds them here when an email bounces, someone unsubscribes or marks you as spam. You can also add your own.") : /*#__PURE__*/React.createElement("div", {
      className: "asp-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "asp-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Address"), /*#__PURE__*/React.createElement("th", null, "Why"), /*#__PURE__*/React.createElement("th", null, "How it happened"), /*#__PURE__*/React.createElement("th", null, "When"), /*#__PURE__*/React.createElement("th", null, "Until"), /*#__PURE__*/React.createElement("th", {
      className: "asp-act",
      "aria-label": "Action"
    }))), /*#__PURE__*/React.createElement("tbody", null, items.map(r => this.renderRow(r, admin))))), total > PAGE ? /*#__PURE__*/React.createElement("div", {
      className: "asp-pager"
    }, /*#__PURE__*/React.createElement(Button, _extends({}, outline, {
      disabled: skip === 0,
      onClick: () => this.load(Math.max(0, skip - PAGE))
    }), "Previous"), /*#__PURE__*/React.createElement("span", null, num(skip + 1), " to ", num(Math.min(skip + PAGE, total)), " of ", num(total)), /*#__PURE__*/React.createElement(Button, _extends({}, outline, {
      disabled: skip + PAGE >= total,
      onClick: () => this.load(skip + PAGE)
    }), "Next")) : null, /*#__PURE__*/React.createElement("p", {
      className: "asp-foot"
    }, "People who asked to stop or marked you as spam can't be added back here. Only Administrators can allow an address again.")), this.renderDialog());
  }
}
export default withRouter(SuppressionComponent);